import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";
import { 
  Calendar, Clock, MapPin, Users, Ticket, Download, CheckCircle, 
  ChevronLeft, X, Share2, Copy, Link, Facebook, Twitter, MessageCircle, Instagram, ExternalLink, Mail
} from "lucide-react";
import { API } from "@/config/api";
import LaserBackground from "@/components/common/LaserBackground";
import CelebrationLasers from "@/components/common/CelebrationLasers";

const EventPage = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [tramosInfo, setTramosInfo] = useState({ enabled: false, visible: false, tramos: [], tramo_actual: null });
  const [eventClosed, setEventClosed] = useState(false);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  // Check if event is closed
  useEffect(() => {
    if (!event) return;
    
    const checkEventStatus = () => {
      // Check if sales are manually closed
      if (event.entradas_activas === false) {
        setEventClosed(true);
        return;
      }
      
      // Check if event time is close (1 minute before)
      if (event.fecha_hora_evento) {
        const eventDate = new Date(event.fecha_hora_evento);
        const now = new Date();
        const timeUntil = eventDate - now;
        setEventClosed(timeUntil < 60000);
      } else {
        setEventClosed(false);
      }
    };
    
    checkEventStatus();
    const interval = setInterval(checkEventStatus, 10000);
    return () => clearInterval(interval);
  }, [event]);

  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await axios.get(`${API}/events/${eventId}`);
        setEvent(response.data);
        
        // Fetch tramos info
        const tramosResponse = await axios.get(`${API}/events/${eventId}/tramos`);
        setTramosInfo(tramosResponse.data);
        
        // Track visit (fire and forget)
        axios.post(`${API}/events/${eventId}/track-visit`).catch(() => {});
      } catch (e) {
        setError("Evento no encontrado");
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [eventId]);

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      // Track share
      axios.post(`${API}/events/${eventId}/track-share`).catch(() => {});
    } catch (e) {
      console.error("Error copying:", e);
    }
  };

  const shareOnFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
    window.open(url, '_blank', 'width=600,height=400');
    // Track share
    axios.post(`${API}/events/${eventId}/track-share`).catch(() => {});
  };

  const shareOnWhatsApp = () => {
    const text = event ? `🎉 ¡Entrada GRATIS para ${event.nombre}!\n📅 ${event.fecha}\n⏰ ${event.hora}\n📍 ${event.sala}\n\n👉 Reserva tu entrada aquí:` : '';
    const url = `https://wa.me/?text=${encodeURIComponent(text + ' ' + shareUrl)}`;
    window.open(url, '_blank');
    // Track share
    axios.post(`${API}/events/${eventId}/track-share`).catch(() => {});
  };

  const shareOnInstagram = () => {
    // Instagram no permite compartir enlaces directamente, copiamos al portapapeles
    navigator.clipboard.writeText(shareUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    alert('¡Enlace copiado! Pégalo en tu historia o publicación de Instagram');
    // Track share
    axios.post(`${API}/events/${eventId}/track-share`).catch(() => {});
  };

  if (loading) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="spinner-large relative z-10"></div>
      </div>
    );
  }

  if (error || !event) {
    return (
      <div className="hero-bg min-h-screen flex items-center justify-center">
        <LaserBackground />
        <div className="text-center relative z-10">
          <XCircle size={64} className="text-[var(--error)] mx-auto mb-4" />
          <h2 className="text-white text-2xl mb-4">{error || "Evento no encontrado"}</h2>
          <button onClick={() => navigate('/')} className="btn-primary">
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  const isSoldOut = event.remaining_tickets <= 0;

  return (
    <div className="hero-bg min-h-screen">
      <LaserBackground />
      <div className="main-container relative z-10 py-8">
        {/* Back Button */}
        <button 
          onClick={() => navigate('/')}
          className="flex items-center gap-2 text-[var(--text-muted)] hover:text-[var(--primary)] mb-6 transition-colors"
          data-testid="back-button"
        >
          <ChevronLeft size={20} />
          <span>Ver todos los eventos</span>
        </button>

        {/* Logo */}
        <div className="text-center mb-6 fade-in-up">
          <h1 className="font-logo text-3xl md:text-4xl text-white neon-glow">
            TAYTANTOS
          </h1>
        </div>

        {/* Price Badge */}
        <div className="text-center mb-6 fade-in-up">
          {event.pago_habilitado && event.precio > 0 ? (
            <span className="inline-block px-6 py-2 bg-gradient-to-r from-[var(--primary)] to-[var(--accent)] text-white font-bold rounded-full text-lg pulse-glow" data-testid="price-badge">
              {event.precio.toFixed(2)} € / entrada
            </span>
          ) : (
            <span className="free-badge pulse-glow" data-testid="free-badge">
              Entrada Gratuita
            </span>
          )}
        </div>

        {/* Event Card */}
        <div className="event-info-box mb-6 fade-in-up" data-testid="event-detail-box">
          {event.flyer_url && (
            <div className="w-full max-w-sm mx-auto mb-4" style={{ aspectRatio: '3/4' }}>
              <img 
                src={event.flyer_url} 
                alt={event.nombre} 
                className="w-full h-full object-cover object-top"
              />
            </div>
          )}
          <h2 className="font-heading text-2xl text-white mb-4 uppercase tracking-wide">
            {event.nombre}
          </h2>
          <div className="space-y-3">
            <div className="event-info-item">
              <MapPin size={18} />
              <span><strong>{event.sala}</strong></span>
            </div>
            <div className="event-info-item">
              <Calendar size={18} />
              <span>{event.fecha}</span>
            </div>
            <div className="event-info-item">
              <Clock size={18} />
              <span>{event.hora}</span>
            </div>
            <a 
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(event.direccion)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="event-info-item text-[var(--accent-cyan)] hover:underline"
            >
              <ExternalLink size={18} />
              <span>{event.direccion}</span>
            </a>
          </div>
        </div>

        {/* Ticket Counter - Solo mostrar si NO hay tramos habilitados */}
        {!(tramosInfo.enabled && tramosInfo.visible) && (
          <div className="ticket-counter mb-6 fade-in-up" data-testid="ticket-counter">
            <div className="flex items-center justify-center gap-2 mb-2">
              <Users size={20} className="text-[var(--accent-cyan)]" />
              <span className="text-xs text-[var(--text-muted)] uppercase tracking-widest">
                Entradas Disponibles
              </span>
            </div>
            <div className="counter-value" data-testid="remaining-count">
              {event.remaining_tickets?.toLocaleString() || 0}
            </div>
            <div className="counter-label">
              de {event.max_tickets?.toLocaleString()} entradas
            </div>
          </div>
        )}

        {/* Event Closed Banner */}
        {eventClosed && (
          <div className="p-4 bg-orange-900/50 border-2 border-orange-500 text-center mb-6" data-testid="event-closed-banner">
            <div className="flex items-center justify-center gap-3 text-orange-400 font-bold text-lg uppercase tracking-wider">
              <span className="inline-block w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
              Entradas no disponibles
              <span className="inline-block w-3 h-3 bg-orange-500 rounded-full animate-pulse"></span>
            </div>
            <p className="text-orange-300 text-sm mt-2">
              {event.mensaje_desactivacion || 'La venta de entradas para este evento ha finalizado'}
            </p>
          </div>
        )}

        {/* Tramo Info */}
        {tramosInfo.enabled && tramosInfo.visible && !eventClosed && (() => {
          // Filtrar solo tramos activos
          const tramosActivos = tramosInfo.tramos.filter(t => t.activo);
          const hayPocasEntradas = tramosActivos.some(t => !t.agotado && t.disponibles <= 150 && t.disponibles > 0);
          const hayMuyPocas = tramosActivos.some(t => !t.agotado && t.disponibles <= 40 && t.disponibles > 0);
          const todosAgotados = tramosActivos.every(t => t.agotado);
          
          return (
            <div className="mb-6 space-y-3" data-testid="tramo-info">
              {/* Banner global de pocas entradas */}
              {hayMuyPocas && !todosAgotados && (
                <div className="p-3 bg-red-900/30 border border-red-500 text-center mb-4 animate-pulse">
                  <div className="flex items-center justify-center gap-2 text-red-400 font-bold text-sm uppercase tracking-wider">
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full animate-ping"></span>
                    ¡Últimas entradas disponibles!
                  </div>
                </div>
              )}
              {hayPocasEntradas && !hayMuyPocas && !todosAgotados && (
                <div className="p-3 bg-orange-900/30 border border-orange-500 text-center mb-4">
                  <div className="flex items-center justify-center gap-2 text-orange-400 font-bold text-sm uppercase tracking-wider">
                    <span className="inline-block w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                    ¡Quedan pocas entradas disponibles!
                  </div>
                </div>
              )}
              
              {/* Mostrar TODOS los tramos activos - Versión compacta */}
              <div className="flex flex-wrap gap-2">
                {tramosActivos.map((tramo, idx) => {
                  const disponibles = tramo.disponibles || 0;
                  const esAgotado = tramo.agotado || disponibles <= 0;
                  const esMuyUrgente = !esAgotado && disponibles <= 40;
                  const esUrgente = !esAgotado && disponibles <= 150;
                  
                  return (
                    <div 
                      key={idx} 
                      className={`px-3 py-2 border-l-2 text-sm ${
                        esAgotado 
                          ? 'bg-gray-800/50 border-gray-500 opacity-60'
                          : esMuyUrgente 
                            ? 'bg-red-900/30 border-red-500' 
                            : esUrgente 
                              ? 'bg-orange-900/30 border-orange-500' 
                              : 'bg-[var(--surface)] border-[var(--accent-cyan)]'
                      }`}
                    >
                      <span className={`font-bold ${esAgotado ? 'text-gray-400' : 'text-white'}`}>
                        {tramo.nombre}
                      </span>
                      {esAgotado && (
                        <span className="ml-2 text-gray-400 text-xs">(Agotado)</span>
                      )}
                      {esMuyUrgente && !esAgotado && (
                        <span className="ml-2 text-red-400 text-xs animate-pulse">¡Pocas!</span>
                      )}
                    </div>
                  );
                })}
              </div>
              
              {/* Todos los tramos agotados */}
              {todosAgotados && tramosActivos.length > 0 && (
                <div className="p-4 bg-red-900/30 border-l-4 border-red-500 mt-4">
                  <p className="text-red-400 font-bold uppercase">Todos los tramos agotados</p>
                  <p className="text-red-300 text-sm mt-1">No quedan entradas disponibles</p>
                </div>
              )}
            </div>
          );
        })()}

        {/* Reserve Button */}
        <div className="text-center mb-8">
          <button 
            onClick={() => navigate('/')}
            className={`btn-primary text-lg px-8 py-4 ${isSoldOut ? 'opacity-50 cursor-not-allowed' : ''}`}
            disabled={isSoldOut}
            data-testid="reserve-event-btn"
          >
            {isSoldOut ? 'Entradas Agotadas' : '🎉 Reservar Mi Entrada Gratis'}
          </button>
        </div>

        {/* Share Section */}
        <div className="form-section fade-in-up" data-testid="share-section">
          <div className="flex items-center gap-3 mb-6">
            <Share2 size={24} className="text-[var(--primary)]" />
            <h3 className="form-title mb-0">Comparte este evento</h3>
          </div>

          {/* Social Share Buttons */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button
              onClick={copyToClipboard}
              className={`flex items-center justify-center gap-2 p-3 ${copied ? 'bg-[var(--success)]' : 'bg-[var(--surface)] border border-[var(--border-color)]'} hover:bg-[var(--primary)] text-white font-bold transition-colors`}
              data-testid="copy-link-btn"
            >
              {copied ? <CheckCircle size={20} /> : <Copy size={20} />}
              <span className="text-sm">{copied ? '¡Copiado!' : 'Copiar'}</span>
            </button>
            
            <button
              onClick={shareOnWhatsApp}
              className="flex items-center justify-center gap-2 p-3 bg-[#25D366] hover:bg-[#128C7E] text-white font-bold transition-colors"
              data-testid="share-whatsapp"
            >
              <MessageCircle size={20} />
              <span className="text-sm">WhatsApp</span>
            </button>
            
            <button
              onClick={shareOnFacebook}
              className="flex items-center justify-center gap-2 p-3 bg-[#1877F2] hover:bg-[#0d5fc7] text-white font-bold transition-colors"
              data-testid="share-facebook"
            >
              <Facebook size={20} />
              <span className="text-sm">Facebook</span>
            </button>
            
            <button
              onClick={shareOnInstagram}
              className="flex items-center justify-center gap-2 p-3 bg-gradient-to-r from-[#833AB4] via-[#FD1D1D] to-[#F77737] hover:opacity-80 text-white font-bold transition-colors"
              data-testid="share-instagram"
            >
              <Instagram size={20} />
              <span className="text-sm">Instagram</span>
            </button>
          </div>
        </div>

        {/* Footer */}
        <footer className="text-center mt-8 pt-8 border-t border-[var(--border-color)]">
          <p className="text-xs text-[var(--text-muted)] mb-4">© 2025 TAYTANTOS - Todos los derechos reservados</p>
          <p className="text-xs text-[var(--text-muted)] mb-4">
            <Mail size={12} className="inline mr-1" />
            <a href="mailto:entradas@taytantos.eu" className="hover:text-[var(--primary)] transition-colors">
              entradas@taytantos.eu
            </a>
          </p>
        </footer>
      </div>
    </div>
  );
};

// Aforo Panel Component (Control de Aforo - Solo visualización)

export default EventPage;
